import React, { useState, useEffect, ChangeEvent } from 'react';
import { FaFilePdf, FaFileWord, FaFileArchive, FaFileImage, FaFileAlt, FaLock, FaDatabase, FaHdd, FaFolder, FaEye, FaDownload, FaTrash } from 'react-icons/fa';
import "./FileManager.css";
import { useTranslation } from 'react-i18next';
import { getBareedyMessages } from '../../services/bareedyServices'; // Adjust path as needed
import LoadingScreen from 'src/Loading/LoadingScreen';

type FileType = {
  id: string;
  name: string;
  title: string;
  type: string;
  size: number;
  vault: string;
  status: string;
  uploadedAt: string;
  uploadedBy: string;
  messageId?: string;
  tags: string[];
  isBodyAttachment: boolean;
  accessLevel: string;
};

const FileManager = () => {
  const { t } = useTranslation();
  // State for files and UI controls
  const [files, setFiles] = useState<FileType[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [vaultFilter, setVaultFilter] = useState<string>('all');
  const [accessFilter, setAccessFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [messages, setMessages] = useState<any[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<any[]>([]);

  // Fetch messages from Bareedy service
  useEffect(() => {
    const fetchMessages = async () => {
      setIsLoading(true);
      try {
        const result = await getBareedyMessages(20, 0);
        // The API returns { success, data, ... }
        setMessages(result?.data || []);
        setFilteredMessages(result?.data || []);
      } catch (error) {
        alert('Failed to load messages.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchMessages();
  }, []);

  // Filter messages based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredMessages(messages);
      return;
    }

    const filtered = messages.filter(msg => {
      const searchLower = searchTerm.toLowerCase();
      
      // Search in subject
      if (msg.subject?.toLowerCase().includes(searchLower)) return true;
      
      // Search in tags
      if (msg.messageTags && msg.messageTags.toLowerCase().includes(searchLower)) return true;
      
      // Search in status
      if (msg.status?.toLowerCase().includes(searchLower)) return true;
      
      // Search in priority
      if (msg.priorityLevel?.toLowerCase().includes(searchLower)) return true;
      
      // Search in tenant names
      if (msg.tenants?.some((t: any) => 
        t.tenant?.name?.toLowerCase().includes(searchLower)
      )) return true;
      
      return false;
    });
    
    setFilteredMessages(filtered);
  }, [searchTerm, messages]);

  // Helper for formatting date
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Get status color configuration
  const getStatusColor = (status: string) => {
    const statusConfig: Record<string, { bg: string; color: string; border: string }> = {
      'delivered': { bg: '#e8f5e9', color: '#2e7d32', border: '1px solid #2e7d32' },
      'done': { bg: '#e8f5e9', color: '#2e7d32', border: '1px solid #2e7d32' },
      'completed': { bg: '#e8f5e9', color: '#2e7d32', border: '1px solid #2e7d32' },
      'active': { bg: '#e8f5e9', color: '#2e7d32', border: '1px solid #2e7d32' },
      'pending': { bg: '#fff3cd', color: '#856404', border: '1px solid #ffeaa7' },
      'started': { bg: '#e3f2fd', color: '#0d47a1', border: '1px solid #bbdefb' },
      'processing': { bg: '#e3f2fd', color: '#0d47a1', border: '1px solid #bbdefb' },
      'failed': { bg: '#fdecea', color: '#c62828', border: '1px solid #ef9a9a' },
      'error': { bg: '#fdecea', color: '#c62828', border: '1px solid #ef9a9a' },
      'cancelled': { bg: '#f5f5f5', color: '#424242', border: '1px solid #bdbdbd' },
      'default': { bg: '#f5f5f5', color: '#424242', border: '1px solid #bdbdbd' }
    };
    
    const normalizedStatus = status.toLowerCase();
    return statusConfig[normalizedStatus] || statusConfig['default'];
  };

  // Get priority color configuration
  const getPriorityColor = (priority: string) => {
    const priorityConfig: Record<string, { bg: string; color: string; border: string }> = {
      'high': { bg: '#fdecea', color: '#c62828', border: '1px solid #ef9a9a' },
      'urgent': { bg: '#fdecea', color: '#c62828', border: '1px solid #ef9a9a' },
      'critical': { bg: '#fdecea', color: '#c62828', border: '1px solid #ef9a9a' },
      'medium': { bg: '#fff3cd', color: '#856404', border: '1px solid #ffeaa7' },
      'normal': { bg: '#e3f2fd', color: '#0d47a1', border: '1px solid #bbdefb' },
      'low': { bg: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' },
      'default': { bg: '#f5f5f5', color: '#424242', border: '1px solid #bdbdbd' }
    };
    
    const normalizedPriority = priority.toLowerCase();
    return priorityConfig[normalizedPriority] || priorityConfig['default'];
  };

  // Filter files based on search and filters
  const filteredFiles = files.filter(file => {
    const matchesSearch = file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         file.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || file.status === statusFilter;
    const matchesVault = vaultFilter === 'all' || file.vault === vaultFilter;
    const matchesAccess = accessFilter === 'all' || file.accessLevel === accessFilter;
    
    return matchesSearch && matchesStatus && matchesVault && matchesAccess;
  });

  // Handle file upload
  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    setUploadedFiles(selectedFiles);
  };

  // Process uploaded files
  const processUpload = () => {
    if (uploadedFiles.length === 0) {
      alert('Please select files to upload.');
      return;
    }

    const newFiles: FileType[] = uploadedFiles.map(file => ({
      id: `FILE-${Date.now()}-${Math.random().toString(36).substring(2, 5)}`,
      name: file.name,
      title: file.name.split('.')[0],
      type: file.type,
      size: file.size,
      vault: 'internal',
      status: 'processing',
      uploadedAt: new Date().toISOString(),
      uploadedBy: 'current.user@org.com',
      tags: [],
      isBodyAttachment: false,
      accessLevel: 'restricted'
    }));

    setFiles([...newFiles, ...files]);
    setUploadedFiles([]);
    alert(`${uploadedFiles.length} file(s) uploaded successfully!`);
  };

  // Delete a file
  const deleteFile = (fileId: string) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      setFiles(files.filter(file => file.id !== fileId));
      alert('File deleted successfully.');
    }
  };

  // Get storage statistics
  const getStorageStats = () => {
    const totalFiles = files.length;
    const internalFiles = files.filter(f => f.vault === 'internal').length;
    const externalFiles = files.filter(f => f.vault === 'external').length;
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    
    return { totalFiles, internalFiles, externalFiles, totalSize };
  };

  const storageStats = getStorageStats();

  return (
    <div className="root" style={{ marginTop: '100px'}}>
      <div className="file-manager">
      
        {/* Filters and Search */}
       

        {/* Bareedy Messages Table */}
        <div className="messages-table-container">
          <h5>{t("onbox.OneBox Message")}</h5>

          <div className="filters-section mt-3">
          <div className="search-box">
            <input
              type="text"
              placeholder={t("onbox.Search in messages...")||""}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
        
        </div>



          {isLoading ? (
            <div className="loading"><LoadingScreen/></div>
          ) : filteredMessages.length === 0 ? (
            <div className="no-files">
              {searchTerm ? `No messages found for "${searchTerm}"` : 'No messages found.'}
            </div>
          ) : (
            <div style={{
              maxWidth: '100%',
              overflowX: 'auto',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              background: '#fff',
             
            }}>
              <table className="files-table" style={{ minWidth: '1200px', borderCollapse: 'collapse' }}>
                <thead style={{
                  position: 'sticky',
                  top: 0,
                  background: '#f8f8f8',
                  zIndex: 2
                }}>
                  <tr>
                    <th style={{ padding: '6px',width:'20%' }}>{t('onbox.Subject')}</th>
                    <th style={{ padding: '6px' }}>{t('onbox.Tags')}</th>
                    <th style={{ padding: '6px' }}>{t('onbox.Status')}</th>
                    <th style={{ padding: '6px' }}>{t('onbox.Priority')}</th>
                    <th style={{ padding: '6px' }}>{t('onbox.Expiry')}</th>
                    <th style={{ padding: '6px' }}>{t('onbox.Files')}</th>
                   
                    {/* <th style={{ padding: '8px' }}>{t('onbox.Actions')}</th> */}
                  </tr>
                </thead>
                <tbody>
                  {filteredMessages.map((msg) => {
                    // Get status and priority colors
                    const statusColor = getStatusColor(msg.status);
                    const priorityColor = getPriorityColor(msg.priorityLevel);

                    return (
                      <tr key={msg.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td>{msg.subject}</td>
                        <td>
                          {msg.messageTags?.split(',').map((tag: string) => (
                            <span key={tag.trim()} className="tag" style={{
                              background: '#e3f2fd',
                              color: '#1976d2',
                              borderRadius: '4px',
                              padding: '2px 6px',
                              marginRight: '4px',
                              fontSize: '12px'
                            }}>{tag.trim()}</span>
                          ))}
                        </td>
                        <td>
                          <span
                            style={{
                              padding: '2px 6px',
                              borderRadius: '16px',
                              background: statusColor.bg,
                              color: statusColor.color,
                              fontSize: '.9rem',
                              border: statusColor.border,
                              fontWeight: 500,
                              display: 'inline-block'
                            }}
                          >
                            {msg.status}
                          </span>
                        </td>
                        <td>
                          <span
                            style={{
                              padding: '2px 6px',
                              borderRadius: '16px',
                              background: priorityColor.bg,
                              color: priorityColor.color,
                              fontSize: '.9rem',
                              border: priorityColor.border,
                              fontWeight: 500,
                              display: 'inline-block'
                            }}
                          >
                            {msg.priorityLevel}
                          </span>
                        </td>
                        <td>{formatDateTime(msg.expiryDate)}</td>
                      
                        <td>
                          {msg.files?.length > 0 ? (
                            msg.files.map((f: any) => (
                              <a
                                key={f.id}
                                href={f.src}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ marginRight: '8px', color: '#1976d2', textDecoration: 'underline',fontSize:'.8rem' }}
                              >
                                <FaDownload style={{ marginRight: '4px' }} />
                              </a>
                            ))
                          ) : (
                            <span>No files</span>
                          )}
                        </td>
                         
                        {/* <td>
                          <button
                            title="View"
                            style={{
                              background: '#e3f2fd',
                              border: 'none',
                              borderRadius: '50%',
                              padding: '6px',
                              marginRight: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            <FaEye color="#1976d2" />
                          </button>
                          <button
                            title="Download"
                            style={{
                              background: '#e8f5e9',
                              border: 'none',
                              borderRadius: '50%',
                              padding: '6px',
                              marginRight: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            <FaDownload color="#43a047" />
                          </button>
                          <button
                            title="Next"
                            style={{
                              background: '#fffde7',
                              border: 'none',
                              borderRadius: '50%',
                              padding: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            <FaFolder color="#fbc02d" />
                          </button>
                        </td> */}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileManager;